"use client";

import { c as _c } from "react/compiler-runtime";
import { membershipFrequencyLabel, useMembershipBillingTerms } from "../memberships/utils";
import { getCatalogPromotionPresentation } from "../promotion-messaging";
import React from "react";
import { FiArrowRight, FiTag } from "react-icons/fi";
import Image from "next/image";
import { PopularBadge } from "@/components/ui/popular-badge";
import QuantityStepper from "@/components/ui/quantity-stepper";
import { useCatalogImageLayout } from "@/contexts/catalog-image-layout-provider";
import { formatCurrency } from "@/lib/format-data";
import { canOptimizeImage, resolveImageUrl } from "@/lib/image-utils";
import { cx } from "@/lib/tailwind-utils";
import { ItemType } from "@/types/enums/item-type";
import { ShopItem } from "@/types/shop";
export type CatalogTileProps = {
  item: ShopItem;
  onSelect?: () => void;
  compact?: boolean;
  className?: string;
  opensDialog?: boolean;
  placement?: "promotion" | "category";
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
};
export type CatalogCardDefinition = {
  label: string;
  Icon: React.ComponentType<{
    className?: string;
  }>;
  visual: {
    background: string;
    Motif?: React.ComponentType;
  };
  eyebrow?: (item: ShopItem) => string;
};
export const SHOP_CATALOG_CARD_HEIGHT_CLASS_NAME = "h-[380px] max-h-[380px]";
export const SHOP_SQUARE_CATALOG_CARD_HEIGHT_CLASS_NAME = "h-auto min-h-[640px] sm:h-[640px] sm:max-h-[640px]";
export const priceForShopItem = (item: ShopItem): number => item.displayPrice ?? item.price;
export const savingsForShopItem = (item: ShopItem): number => {
  const original = item.originalPrice ?? item.price;
  return Math.max(0, original - priceForShopItem(item));
};
export const hasPromotion = (item: ShopItem): boolean => Boolean(item.bestPromotion || (item.availablePromotions?.length ?? 0) > 0 || (item.applicablePromotions?.length ?? 0) > 0);
export const CornerRibbon: React.FC<{
  compact?: boolean;
  iconClassName?: string;
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(14);
  const {
    compact: t1,
    iconClassName,
    children
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const t2 = compact ? "-left-[24px] top-[15px] h-[22px] w-[100px]" : "-left-[20px] top-[17px] h-[24px] w-[100px]";
  let t3;
  if ($[0] !== t2) {
    t3 = cx("absolute -rotate-45 bg-gradient-to-r from-transparent via-background/80 to-transparent", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-background/60" />;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t3) {
    t5 = <div className={t3}>{t4}</div>;
    $[3] = t3;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = compact ? "left-[15px] top-[18px] h-5 w-5" : "left-[17px] top-[19px] h-6 w-6";
  let t7;
  if ($[5] !== iconClassName || $[6] !== t6) {
    t7 = cx("absolute flex -rotate-45 items-center justify-center text-primary", t6, iconClassName);
    $[5] = iconClassName;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== children || $[9] !== t7) {
    t8 = <div className={t7}>{children}</div>;
    $[8] = children;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t5 || $[12] !== t8) {
    t9 = <div aria-hidden={true} data-shop-corner-ribbon={true} className="pointer-events-none absolute inset-0">{t5}{t8}</div>;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
};
export const TypeCorner: React.FC<{
  definition: CatalogCardDefinition;
  compact?: boolean;
}> = t0 => {
  const $ = _c(6);
  const {
    definition,
    compact: t1
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const Icon = definition.Icon;
  const t2 = compact ? "h-4 w-4" : "h-5 w-5";
  let t3;
  if ($[0] !== Icon || $[1] !== t2) {
    t3 = <Icon className={t2} />;
    $[0] = Icon;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== compact || $[4] !== t3) {
    t4 = <CornerRibbon compact={compact}>{t3}</CornerRibbon>;
    $[3] = compact;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
export const ImageWell: React.FC<{
  item: ShopItem;
}> = t0 => {
  const $ = _c(32);
  const {
    item
  } = t0;
  const {
    catalogImageLayout
  } = useCatalogImageLayout();
  const usesSquareImage = catalogImageLayout === "SQUARE";
  if (!item.imageUrl) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="h-16 p-3 pb-4" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== item.imageUrl || $[2] !== item.name || $[3] !== usesSquareImage) {
    const src = resolveImageUrl(item.imageUrl);
    t7 = true;
    t8 = usesSquareImage ? "square" : "wide";
    const t10 = usesSquareImage ? "p-3 pb-4" : "h-40 p-3 pb-4";
    if ($[14] !== t10) {
      t9 = cx(t10);
      $[14] = t10;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    T0 = Image;
    t2 = src;
    t3 = item.name;
    t4 = 320;
    t5 = 320;
    t6 = usesSquareImage ? "(min-width: 640px) 320px, 100vw" : "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";
    t1 = canOptimizeImage(src);
    $[1] = item.imageUrl;
    $[2] = item.name;
    $[3] = usesSquareImage;
    $[4] = T0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    T0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  const t10 = !t1;
  const t11 = usesSquareImage ? "mx-auto aspect-square max-w-[320px] bg-background object-contain" : "h-full object-cover";
  let t12;
  if ($[16] !== t11) {
    t12 = cx("w-full rounded-xl", t11);
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== T0 || $[19] !== t10 || $[20] !== t12 || $[21] !== t2 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6) {
    t13 = <T0 src={t2} alt={t3} width={t4} height={t5} sizes={t6} unoptimized={t10} loading="lazy" className={t12} />;
    $[18] = T0;
    $[19] = t10;
    $[20] = t12;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t14 = <div data-shop-image-well={t7} data-shop-image-layout={t8} className={t9}>{t13}</div>;
    $[27] = t13;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
};
export const CatalogCardFrame: React.FC<CatalogTileProps & {
  definition: CatalogCardDefinition;
  corner: React.ReactNode;
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(36);
  const {
    item,
    onSelect,
    compact: t1,
    className,
    definition,
    corner,
    children,
    opensDialog: t2,
    placement,
    quantity,
    onQuantityChange
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const opensDialog = t2 === undefined ? false : t2;
  const {
    catalogImageLayout
  } = useCatalogImageLayout();
  const usesSquareLayout = catalogImageLayout === "SQUARE";
  const interactive = Boolean(onSelect);
  const hasQuantityControl = quantity !== undefined && Boolean(onQuantityChange);
  const Root = interactive && !hasQuantityControl ? "button" : "div";
  let t3;
  if ($[0] !== hasQuantityControl || $[1] !== interactive || $[2] !== onSelect) {
    t3 = interactive && !hasQuantityControl ? {
      type: "button" as const,
      onClick: onSelect
    } : {};
    $[0] = hasQuantityControl;
    $[1] = interactive;
    $[2] = onSelect;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const rootProps = t3;
  const Motif = definition.visual.Motif;
  const t4 = interactive && opensDialog ? "dialog" : undefined;
  const t5 = item.id;
  const t6 = interactive && "cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_28px_-4px_rgb(0_0_0_/_0.25),0_18px_48px_-12px_rgb(0_0_0_/_0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";
  const t7 = compact ? "min-h-[230px]" : "min-h-[250px]";
  let t8;
  if ($[4] !== className || $[5] !== definition.visual.background || $[6] !== t6 || $[7] !== t7 || $[8] !== usesSquareLayout) {
    t8 = cx("group relative flex w-full flex-col overflow-hidden rounded-2xl text-left", "shadow-[0_2px_8px_-2px_rgb(0_0_0_/_0.15),0_10px_24px_-12px_rgb(0_0_0_/_0.25)]", "transition-[transform,box-shadow,border-color] duration-500 ease-out", definition.visual.background, t6, t7, usesSquareLayout ? SHOP_SQUARE_CATALOG_CARD_HEIGHT_CLASS_NAME : cx("h-full", SHOP_CATALOG_CARD_HEIGHT_CLASS_NAME), className);
    $[4] = className;
    $[5] = definition.visual.background;
    $[6] = t6;
    $[7] = t7;
    $[8] = usesSquareLayout;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== Motif) {
    t9 = Motif && <div aria-hidden={true} className="pointer-events-none absolute inset-0"><Motif /></div>;
    $[10] = Motif;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== hasQuantityControl || $[13] !== interactive || $[14] !== item.name || $[15] !== onSelect || $[16] !== opensDialog) {
    t10 = interactive && hasQuantityControl && <button type="button" aria-label={`View ${item.name}`} aria-haspopup={opensDialog ? "dialog" : undefined} className="absolute inset-0 z-10 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background" onClick={onSelect} />;
    $[12] = hasQuantityControl;
    $[13] = interactive;
    $[14] = item.name;
    $[15] = onSelect;
    $[16] = opensDialog;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="pointer-events-none absolute inset-3 rounded-xl border border-primary/15" />;
    t12 = <div className="pointer-events-none absolute inset-[14px] rounded-[10px] border border-dashed border-primary/15" />;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  let t13;
  if ($[20] !== item) {
    t13 = <ImageWell item={item} />;
    $[20] = item;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== children) {
    t14 = <div className="flex min-h-0 flex-1 flex-col p-5 pt-0">{children}</div>;
    $[22] = children;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== Root || $[25] !== corner || $[26] !== item.id || $[27] !== placement || $[28] !== rootProps || $[29] !== t10 || $[30] !== t13 || $[31] !== t14 || $[32] !== t4 || $[33] !== t8 || $[34] !== t9) {
    t15 = <Root {...rootProps} aria-haspopup={t4} data-shop-item-id={t5} data-shop-placement={placement} className={t8}>{t9}{t10}{t11}{t12}{corner}{t13}{t14}</Root>;
    $[24] = Root;
    $[25] = corner;
    $[26] = item.id;
    $[27] = placement;
    $[28] = rootProps;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
    $[32] = t4;
    $[33] = t8;
    $[34] = t9;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
};
export const CatalogItemHeading: React.FC<{
  item: ShopItem;
  definition: CatalogCardDefinition;
  showFullDescription?: boolean;
}> = t0 => {
  const $ = _c(26);
  const {
    item,
    definition,
    showFullDescription: t1
  } = t0;
  const showFullDescription = t1 === undefined ? false : t1;
  const {
    catalogImageLayout
  } = useCatalogImageLayout();
  const hasImage = Boolean(item.imageUrl);
  const hasWideImage = hasImage && catalogImageLayout === "WIDE";
  let t2;
  if ($[0] !== definition || $[1] !== item) {
    t2 = definition.eyebrow?.(item) ?? item.category ?? definition.label;
    $[0] = definition;
    $[1] = item;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const eyebrow = t2;
  let t3;
  if ($[3] !== eyebrow) {
    t3 = <p className="min-w-0 line-clamp-1 font-semibold uppercase text-primary/70 text-micro tracking-[0.24em]" title={eyebrow}>{eyebrow}</p>;
    $[3] = eyebrow;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== item.isPopular) {
    t4 = item.isPopular && <PopularBadge tooltip={null} />;
    $[5] = item.isPopular;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="mb-2 flex min-h-4 items-center gap-2">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = hasWideImage ? "line-clamp-1" : "line-clamp-2";
  let t7;
  if ($[10] !== t6) {
    t7 = cx("font-serif text-[1.9rem] font-medium leading-[1.08] tracking-tight text-foreground transition-colors group-hover:text-primary", t6);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== item.name || $[13] !== t7) {
    t8 = <h3 className={t7} title={item.name}>{item.name}</h3>;
    $[12] = item.name;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== hasImage || $[16] !== hasWideImage || $[17] !== item.description || $[18] !== showFullDescription) {
    t9 = item.description && (showFullDescription ? <p className="mt-3 whitespace-pre-line text-caption leading-5 text-foreground-secondary">{item.description}</p> : <p className={cx("mt-3 text-caption leading-5 text-foreground-secondary", hasWideImage ? "line-clamp-1" : hasImage ? "line-clamp-2" : "line-clamp-6")} title={item.description}>{item.description}</p>);
    $[15] = hasImage;
    $[16] = hasWideImage;
    $[17] = item.description;
    $[18] = showFullDescription;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <div className="min-h-0 flex-1 overflow-hidden">{t8}{t9}</div>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t5) {
    t11 = <>{t5}{t10}</>;
    $[23] = t10;
    $[24] = t5;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
};
type ItemPriceProps = {
  currentPrice: number;
  originalPrice: number;
  savings: number;
};
const StandardPrice: React.FC<ItemPriceProps> = t0 => {
  const $ = _c(10);
  const {
    currentPrice,
    originalPrice,
    savings
  } = t0;
  let t1;
  if ($[0] !== currentPrice) {
    t1 = formatCurrency(currentPrice);
    $[0] = currentPrice;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="block font-serif text-2xl font-medium leading-none tracking-tight text-foreground tabular-nums">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== originalPrice || $[5] !== savings) {
    t3 = savings > 0 && <span className="text-caption text-foreground-secondary line-through tabular-nums">{formatCurrency(originalPrice)}</span>;
    $[4] = originalPrice;
    $[5] = savings;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex flex-wrap items-baseline gap-2">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};

// Renewing plans lead with the per-cycle price; `item.price` is the first charge,
// which folds in the setup fee and any first-cycle price.
const MembershipPrice: React.FC<ItemPriceProps & {
  item: ShopItem;
}> = t0 => {
  const $ = _c(16);
  const {
    item,
    ...price
  } = t0;
  const {
    effectivelyRenews,
    chargeLabel
  } = useMembershipBillingTerms(item);
  const {
    currentPrice,
    originalPrice,
    savings
  } = price;
  if (!effectivelyRenews || typeof item.recurringPrice !== "number" || !item.billingFrequency) {
    let t1;
    if ($[0] !== item.billingFrequency) {
      t1 = item.billingFrequency && <p className="mt-1 text-caption font-semibold text-primary">{membershipFrequencyLabel(item.billingFrequency)} billing</p>;
      $[0] = item.billingFrequency;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return <><StandardPrice {...price} />{t1}</>;
  }
  let t1;
  if ($[2] !== item.recurringPrice) {
    t1 = formatCurrency(item.recurringPrice);
    $[2] = item.recurringPrice;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <span className="block font-serif text-2xl font-medium leading-none tracking-tight text-foreground tabular-nums">{t1}</span>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== item.billingFrequency) {
    t3 = membershipFrequencyLabel(item.billingFrequency);
    $[6] = item.billingFrequency;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <span className="text-caption font-semibold text-primary">{t3}</span>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <div className="flex flex-wrap items-baseline gap-2">{t2}{t4}</div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = (currentPrice !== item.recurringPrice || savings > 0) && <p className="mt-1 text-caption text-foreground-secondary tabular-nums"><span className="font-semibold text-foreground">{formatCurrency(currentPrice)}</span>{savings > 0 && <span className="ml-1 line-through">{formatCurrency(originalPrice)}</span>}<span> {chargeLabel.toLowerCase()}</span>{item.setupFee ? <span>{` · includes ${formatCurrency(item.setupFee)} one-time setup fee`}</span> : null}</p>;
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
export const PriceFooter: React.FC<{
  item: ShopItem;
  interactive: boolean;
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
  statusLabel?: string;
}> = t0 => {
  const $ = _c(33);
  const {
    item,
    interactive,
    quantity,
    onQuantityChange,
    statusLabel
  } = t0;
  let t1;
  if ($[0] !== item) {
    t1 = priceForShopItem(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const currentPrice = t1;
  const originalPrice = item.originalPrice ?? item.price;
  let t2;
  if ($[2] !== item) {
    t2 = savingsForShopItem(item);
    $[2] = item;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const savings = t2;
  const packageSavings = item.packageSavings ?? 0;
  let t3;
  if ($[4] !== item) {
    t3 = getCatalogPromotionPresentation(item);
    $[4] = item;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const promotion = t3;
  let t4;
  if ($[6] !== promotion) {
    t4 = promotion?.kind === "available" && <p className="mb-2 flex min-h-4 items-start gap-1.5 text-caption leading-4 text-foreground-secondary" title={promotion.name}><FiTag aria-hidden={true} className="mt-px h-3.5 w-3.5 flex-none text-primary" /><span className="line-clamp-2"><span className="font-semibold text-primary">{promotion.benefit}</span>{` · ${promotion.action}`}</span></p>;
    $[6] = promotion;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== currentPrice || $[9] !== item || $[10] !== originalPrice || $[11] !== promotion?.kind || $[12] !== savings) {
    t5 = promotion?.kind === "gift" ? <span className="text-caption text-foreground-secondary line-through tabular-nums">{formatCurrency(originalPrice)}</span> : item.type === ItemType.MEMBERSHIP ? <MembershipPrice item={item} currentPrice={currentPrice} originalPrice={originalPrice} savings={savings} /> : <StandardPrice currentPrice={currentPrice} originalPrice={originalPrice} savings={savings} />;
    $[8] = currentPrice;
    $[9] = item;
    $[10] = originalPrice;
    $[11] = promotion?.kind;
    $[12] = savings;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== onQuantityChange || $[15] !== packageSavings || $[16] !== promotion || $[17] !== quantity) {
    t6 = promotion?.kind === "applied" || promotion?.kind === "gift" ? <p className="text-caption text-foreground-secondary"><span className="font-semibold text-primary">{promotion.benefit}</span>{(promotion.kind === "gift" || quantity === undefined || !onQuantityChange) && <> {promotion.action}</>}</p> : packageSavings > 0 ? <p className="text-caption font-semibold text-primary">you save {formatCurrency(packageSavings)}</p> : null;
    $[14] = onQuantityChange;
    $[15] = packageSavings;
    $[16] = promotion;
    $[17] = quantity;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <div className="min-w-0">{t5}{t6}</div>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== interactive || $[23] !== onQuantityChange || $[24] !== quantity || $[25] !== statusLabel) {
    t8 = quantity !== undefined && onQuantityChange ? <div className="relative z-20 ml-auto" onClick={_temp}><QuantityStepper value={quantity} onChange={onQuantityChange} className="bg-background/90" /></div> : statusLabel ? <span className="ml-auto pb-1 text-caption font-semibold text-foreground">{statusLabel}</span> : interactive && <span className="pointer-events-none ml-auto inline-flex items-center gap-1.5 pb-1 text-caption font-semibold text-primary">Select<FiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>;
    $[22] = interactive;
    $[23] = onQuantityChange;
    $[24] = quantity;
    $[25] = statusLabel;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 border-t border-primary/10">{t7}{t8}</div>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t4 || $[31] !== t9) {
    t10 = <div className="mt-auto shrink-0 pt-2">{t4}{t9}</div>;
    $[30] = t4;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
};
export const CatalogTileSkeleton: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={cx("relative min-h-[230px] w-full animate-pulse overflow-hidden rounded-2xl bg-muted", SHOP_CATALOG_CARD_HEIGHT_CLASS_NAME)}><div className="pointer-events-none absolute inset-3 rounded-xl border border-foreground/5" /><div className="absolute left-5 top-5 h-4 w-24 rounded-full bg-background/60" /><div className="absolute right-5 top-14 h-20 w-20 rounded-xl bg-background/60" /><div className="absolute left-5 right-32 top-20 h-7 rounded bg-background/60" /><div className="absolute left-5 right-24 top-32 h-4 rounded bg-background/60" /><div className="absolute bottom-5 left-5 h-8 w-24 rounded bg-background/60" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(event) {
  return event.stopPropagation();
}